import { RestError } from "../../services/rest-service/RestService";
import { useMutation, useQueryClient } from "react-query";
import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { AccessType } from "../../common/enums/DRCXEnums";
import { AccessPermission } from "../../common/models/AccessPermission";
import { PageConfigurationModel4, PageConfigurationAccessEnum, PageConfigurationDataModel3, PageConfigurationModel3, PageConfigurationTypeEnum } from "../../services/page-configuration-service/Models";
import PageConfigurationService from "../../services/page-configuration-service/PageConfigurationService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";

import { keys } from "./Keys";

import {keys as deviceSummaryKeys} from "../group-summary/devices/Keys";
import { keys as wellSummaryKeys } from "../group-summary/wells/Keys";


export type MutationContextType = {
    previousConfigurations: PageConfigurationDataModel3 | undefined;
};

export type UseSavePageConfigurationParameters = {
    groupID: number,
} & BaseUseMutationParameters<PageConfigurationModel4, PageConfigurationModel4>;

export function useSavePageConfiguration({
    groupID,
    onError,
    onSuccess
}: UseSavePageConfigurationParameters) {
    const queryClient = useQueryClient();



    return useMutation<PageConfigurationModel4, RestError, PageConfigurationModel4, MutationContextType>(
        (configuration) => {
            return PageConfigurationService.savePageConfiguration(
                groupID,
                configuration
            );
        },
        {
            onMutate: async (configuration: PageConfigurationModel4) => {

                const otherKeys = configuration.pageConfigurationType === PageConfigurationTypeEnum.SummaryListing_RTS ?
                    deviceSummaryKeys :
                    wellSummaryKeys;                

                await queryClient.cancelQueries(keys.all);
                await queryClient.cancelQueries(otherKeys.all);


                //const previousConfiguration = queryClient.getQueryData<PageConfigurationModel4>(deviceSummaryKeys.configurationList(groupID));
                // queryClient.setQueryData<PageConfigurationModel4>(keys.configuration(groupID, configuration.id)),
                // (old?: PageConfigurationModel4) => {


                //     return {
                //         previousConfiguration;
                //     }
                // });


                const previousConfigurations = queryClient.getQueryData<PageConfigurationDataModel3>(
                    otherKeys.configurationList(
                        groupID
                    )
                );
                queryClient.setQueryData<PageConfigurationDataModel3>(
                    otherKeys.configurationList(
                        groupID
                    ),
                    (old?: PageConfigurationDataModel3) => {

                        const configurations = (old?.privateConfigurations ?? []).concat(old?.sharedConfigurations ?? []);

                        const existingConfiguration = configurations?.find(cfg => cfg.id === configuration.id);

                        const newModel = new PageConfigurationModel3({
                            id: configuration.id,
                            name: configuration.name,
                            pcType: configuration.pageConfigurationType,
                            access: configuration.access === AccessType.Private ? 
                                        PageConfigurationAccessEnum.Private : 
                                    configuration.access === AccessType.Shared ? 
                                        PageConfigurationAccessEnum.Shared : 
                                        PageConfigurationAccessEnum.SharedReadOnly,
                            userPermissions: configuration.userPermissions
                        });

                        if (old) {
                            const copy = { ...old };
                            if (existingConfiguration) {
                                if (newModel.access === PageConfigurationAccessEnum.Private) {
                                    copy.privateConfigurations = copy.privateConfigurations ?
                                                                    copy.privateConfigurations?.map(o => o.id === configuration.id ? newModel : o) :
                                                                    [newModel];
                                } else {
                                    copy.sharedConfigurations = copy.sharedConfigurations ? 
                                                                    copy.sharedConfigurations?.map(o => o.id === configuration.id ? newModel : o) :
                                                                    [newModel];
                                }
                            }
                            return copy;
                        } else {

                            return new PageConfigurationDataModel3({
                                userPermissions: [new AccessPermission({
                                    functionalityType: AccessFunctionalityTypeEnum.PageConfiguration,
                                    access: 65535
                                })],
                                privateConfigurations: newModel.access === PageConfigurationAccessEnum.Private ? [newModel] : [],
                                sharedConfigurations: newModel.access !== PageConfigurationAccessEnum.Private ? [newModel] : []
                            });
                        }
                    }
                );

                return {
                    previousConfigurations
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _configuration, context) => {

                const otherKeys = _configuration.pageConfigurationType === PageConfigurationTypeEnum.SummaryListing_RTS ?
                deviceSummaryKeys :
                wellSummaryKeys;                

                if (error && context?.previousConfigurations) {

                    queryClient.setQueryData<PageConfigurationDataModel3>(
                        otherKeys.configurationList(
                            groupID
                        ),
                        context?.previousConfigurations
                    );
                }

                // If there was an error in saving our configuration, such as name in use,
                // then don't invalidate the queries, otherwise the configuration will
                // reload and our changes will be discarded.
                if (!error) {
                    queryClient.invalidateQueries(keys.all);
                    queryClient.invalidateQueries(otherKeys.all);
                }
            }
        }
    );
}
